import * as yup from 'yup'; 
import { getRequest, postRequest, putRequest } from '../../../services/httpService';
import { Files, LaparoscopyDetailsEnum } from '../laparoscopy/laparoscopy.helper';

// Enums for Dropdown values
export type BlankEnum = '';
export type NullEnum = 'NULL';
export type IndicationEnum = 'ANEJACULATION'| 'IMPOTENCE'| 'SECRETORY_AZOOSPERMIA'| 'SEVERE_OLIGOASTHENOTERATOZOOSPERMIA'| 'INCOMPLETE_SPERMATOGENIC_ARREST'| 'CONGENITAL_BILATERAL_ABSENCE_OF_VAS_DEFERNS'| 'FAILED_REVERSAL_OF_VASECTOMY'| 'BLOCKED_VAS_FOLLOWING_GENITAL_TRACT_INFECTIONS'| 'EJACULATORY_DUCT_OBSTRUCTION'| 'BLOCKED_VAS_IN_KARTAGENER_S_SYNDROME'| 'YOUNG_S_SYNDROME';

export type AnesthesiaEnum = 'GENERAL_ANESTHESIA'| 'REGIONAL_ANESTHESIA'| 'LOCAL_ANESTHESIA'| 'MONITORED_ANESTHESIA_CARE_MAC_SEDATION'| 'SPINAL_ANESTHESIA'| 'EPIDURAL_ANESTHESIA';

export type MethodOfSurgicalSpermRetrievalEnum = 'TESA'| 'PESA'| 'MESA'| 'MICRO_TESE'| 'TESE'| 'PESE';

export type SiteEnum = 'RIGHT_TESTIS' | 'LEFT_TESTIS' | 'BOTH';
export type SpecimenTypeEnum = 'TISSUE' | 'ASPIRATE';



// Surgical Sperm Retrieval Interface
export interface SurgicalSpermRetrievalType {
  id?: number | null;
  patient_procedure: number;
  date: string;
  time: string;
  indication: IndicationEnum | BlankEnum | NullEnum;
  clinician: number | null;
  specimen_type: SpecimenTypeEnum | BlankEnum | NullEnum;
  method_of_surgical_sperm_retrieval: MethodOfSurgicalSpermRetrievalEnum | BlankEnum | NullEnum;
  surgeon: number | null;
  anesthetist: number | null;
  anesthesia: AnesthesiaEnum | BlankEnum | NullEnum;
  andrologist: number | null;
  witness_andrologist: number | null;
  site: SiteEnum | BlankEnum | NullEnum;

  left_sperm_count: number | null;
  left_motility: number | null;
  left_remark: string | null;

  right_sperm_count: number | null;
  right_motility: number | null;
  right_remark: string | null;

  remarks: string | null;
  complications: string | null;
  clinician_name?: string | null;
  surgeon_name?: string | null;
  anesthetist_name?: string | null;
  andrologist_name?: string | null;
  witness_andrologist_name?: string | null;
  images: Files[]
}


// Initial State
export const initialSurgicalSpermRetrieval: SurgicalSpermRetrievalType = {
  patient_procedure: 0,
  date: '',
  time: '',
  indication: '',
  clinician: null,
  specimen_type: '',
  method_of_surgical_sperm_retrieval: '',
  surgeon: null,
  anesthetist: null,
  anesthesia: '',
  andrologist: null,
  witness_andrologist: null,
  site: '',
  left_sperm_count: null,
  left_motility: null,
  left_remark: '',
  right_sperm_count: null,
  right_motility: null,
  right_remark: '',
  remarks: '',
  complications: '',
  images: []
};


// Validation Schema
export const SurgicalSpermRetrievalValidationSchema = yup.object({
  date: yup.string().required('Date is required'),
  time: yup.string().required('Time is required'),
  indication: yup
    .string()
    .required('Indication is required')
    .oneOf(
      [
        'ANEJACULATION',
        'IMPOTENCE',
        'SECRETORY_AZOOSPERMIA',
        'SEVERE_OLIGOASTHENOTERATOZOOSPERMIA',
        'INCOMPLETE_SPERMATOGENIC_ARREST',
        'CONGENITAL_BILATERAL_ABSENCE_OF_VAS_DEFERNS',
        'FAILED_REVERSAL_OF_VASECTOMY',
        'BLOCKED_VAS_FOLLOWING_GENITAL_TRACT_INFECTIONS',
        'EJACULATORY_DUCT_OBSTRUCTION',
        'BLOCKED_VAS_IN_KARTAGENER_S_SYNDROME',
        'YOUNG_S_SYNDROME',
      ],
      'Invalid Indication'
    ),
  clinician: yup.number().nullable(),
  specimen_type: yup
    .string()
    .nullable()
    .oneOf(['TISSUE', 'ASPIRATE', 'UNKNOWN'], 'Invalid Specimen Type'),
  method_of_surgical_sperm_retrieval: yup
    .string()
    .nullable()
    .oneOf(['TESA', 'PESA', 'MESA', 'MICRO_TESE', 'TESE', 'PESE'], 'Invalid Method'),
  surgeon: yup.number().nullable(),
  anesthetist: yup.number().nullable(),
  anesthesia: yup
    .string()
    .nullable()
    .oneOf(
      [
        'GENERAL_ANESTHESIA',
        'REGIONAL_ANESTHESIA',
        'LOCAL_ANESTHESIA',
        'MONITORED_ANESTHESIA_CARE_MAC_SEDATION',
        'SPINAL_ANESTHESIA',
        'EPIDURAL_ANESTHESIA',
      ],
      'Invalid Anesthesia'
    ),
  andrologist: yup.number().nullable(),

  witness_andrologist: yup
    .number()
    .required('Witness Andrologist name is required')
    .test('not-same', 'The witness andrologist cannot be the same as the andrologist.', function (value) {
      const { andrologist } = this.parent; // Accessing the sibling field
      return value !== andrologist; // Validate that they are not the same
    }),

    
  site: yup
    .string()
    .nullable()
    .oneOf(['RIGHT_TESTIS', 'LEFT_TESTIS', 'BOTH'], 'Invalid Site'),

  left_sperm_count: yup.number().nullable(),
  left_motility: yup.number().nullable(),
  left_remark: yup.string().nullable().max(1000, 'Max 1000 characters'),

  right_sperm_count: yup.number().nullable(),
  right_motility: yup.number().nullable(),
  right_remark: yup.string().nullable().max(1000, 'Max 1000 characters'),

  remarks: yup.string().nullable().max(1000, 'Max 1000 characters'),
  complications: yup.string().nullable().max(1000, 'Max 1000 characters'),
});


export function generateSurgicalSpermRetrievalURL(id: number): string {
  return `surgical-sperm-retrieval/patient-procedure/${id}`;
}

export function addSurgicalSpermRetrieval(data: SurgicalSpermRetrievalType): Promise<SurgicalSpermRetrievalType> {
  const url = 'surgical-sperm-retrieval/';
  return postRequest(url, data, { responseType: 'json' });
}


export function getSurgicalSpermRetrieval(id: number): Promise<SurgicalSpermRetrievalType> {
  return getRequest(generateSurgicalSpermRetrievalURL(id), { responseType: 'json' });
}

export function updateSurgicalSpermRetrieval(id: number, data: SurgicalSpermRetrievalType): Promise<SurgicalSpermRetrievalType> {
  const url = `/surgical-sperm-retrieval/${id}`;
  return putRequest(url, data, { responseType: 'json' });
}

export const indicationItems: { value: IndicationEnum; label: string }[] = [
  { value: 'ANEJACULATION', label: 'indicationAnejaculation' },
  { value: 'IMPOTENCE', label: 'indicationImpotence' },
  { value: 'SECRETORY_AZOOSPERMIA', label: 'indicationSecretoryAzoospermia' },
  { value: 'SEVERE_OLIGOASTHENOTERATOZOOSPERMIA', label: 'indicationSevereOligoasthenoteratozoospermia' },
  { value: 'INCOMPLETE_SPERMATOGENIC_ARREST', label: 'indicationIncompleteSpermatogenicArrest' },
  { value: 'CONGENITAL_BILATERAL_ABSENCE_OF_VAS_DEFERNS', label: 'indicationCongenitalBilateralAbsenceOfVasDeferns' },
];

export const anesthesiaOptions: { value: AnesthesiaEnum; label: string }[] = [
  { value: 'GENERAL_ANESTHESIA', label: 'anesthesiaGeneralAnesthesia' },
  { value: 'REGIONAL_ANESTHESIA', label: 'anesthesiaRegionalAnesthesia' },
  { value: 'LOCAL_ANESTHESIA', label: 'anesthesiaLocalAnesthesia' },
  { value: 'MONITORED_ANESTHESIA_CARE_MAC_SEDATION', label: 'anesthesiaMonitoredAnesthesiaCareMacSedation' },
  { value: 'SPINAL_ANESTHESIA', label: 'anesthesiaSpinalAnesthesia' },
  { value: 'EPIDURAL_ANESTHESIA', label: 'anesthesiaEpiduralAnesthesia' },
];
 
export const specimenTypeItems: { value: SpecimenTypeEnum; label: string }[] = [
  { value: 'TISSUE', label: 'specimenTypeTissue' },
  { value: 'ASPIRATE', label: 'specimenTypeAspirate' },
];

export const methodOfSurgicalSpermRetrievalItems: { value: MethodOfSurgicalSpermRetrievalEnum; label: string }[] = [
  { value: 'TESA', label: 'methodOfSurgicalSpermRetrievalTESA' },
  { value: 'PESA', label: 'methodOfSurgicalSpermRetrievalPESA' },
  { value: 'MESA', label: 'methodOfSurgicalSpermRetrievalMESA' },
  { value: 'MICRO_TESE', label: 'methodOfSurgicalSpermRetrievalMicroTESE' },
  { value: 'TESE', label: 'methodOfSurgicalSpermRetrievalTESE' },
  { value: 'PESE', label: 'methodOfSurgicalSpermRetrievalPESE' },
];

export const siteItems: { value: SiteEnum; label: string }[] = [
  { value: 'RIGHT_TESTIS', label: 'siteRightTestis' },
  { value: 'LEFT_TESTIS', label: 'siteLeftTestis' },
  { value: 'BOTH', label: 'siteBoth' },
];
 
export function getSurgicalSpermRetrievalList(id: number): Promise<LaparoscopyDetailsEnum> {
  const url = `patient-procedure/?patient=${id}&search=Surgical Sperm Retrieval`;
  return getRequest(url, { responseType: 'json' });
}

// Function to delete attachment from surgical sperm retrieval
export const deleteSurgicalSpermRetrievalAttachment = (
  attachmentId: number
): Promise<void> => {
  const url = '/surgical-sperm-retrieval/surgical_sperm_retrieval_file_delete/';
  return postRequest(url, { id: attachmentId });
};




// Define a constant for roles to avoid magic strings
export const ROLES = ['DOCTOR', 'ANESTHETIST', 'ANDROLOGIST'] as const;

export interface Clinician {
  id: number; 
  first_name: string;
  last_name: string;
}

// Create a type that represents the role strings
export type Role = typeof ROLES[number]; 

